import * as React from "react";
import { ShieldCheck, Award, ArrowRight, Info } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
const timelineSteps = [{
  title: "Weeks 1-2: Neonatal Care",
  description: "Round-the-clock monitoring, Early Neurological Stimulation (ENS) introduction, and ensuring optimal nursing in a quiet, temperature-controlled environment."
}, {
  title: "Weeks 3-4: Transitional Period",
  description: "Eyes and ears open, wobbly walking begins, and gentle introduction to high-quality soft foods alongside nursing. Deworming regimen begins."
}, {
  title: "Weeks 5-7: Socialization & Weaning",
  description: "Active playtime, introduction to litterbox training, and careful exposure to varied household sights, sounds, and surfaces."
}, {
  title: "Weeks 8-10: Veterinary Clearance",
  description: "Comprehensive health exams by our licensed veterinarian, administration of first core vaccines, and microchipping. Conformation evaluations begin and are compared with parents and generations before."
}, {
  title: "Weeks 11-12: Ready for Home",
  description: "Final behavioral evaluations and careful matching with approved forever families to ensure a perfect, lifelong fit. 12 Week vaccine given."
}];
export default function MustRead() {
  return <section id="mustread" className="w-full bg-background py-16 md:py-24 lg:py-32">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {}
          <div className="lg:col-span-7 space-y-12">
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="rounded-none border-primary text-primary font-sans uppercase tracking-wider px-3 py-1">Puppy Adoption Guide</Badge>
              </div>
              
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground font-sans uppercase">
                Must Read
              </h2>
              
              <div className="columns-1 md:columns-2 gap-8 text-muted-foreground font-sans leading-relaxed text-lg">
                <p className="mb-6 md:mb-0">
                  At Mile High Cavaliers, we believe in complete transparency, rigorous health testing, and the highest standards of canine care. Before bringing one of our puppies into your home, it is crucial to understand our meticulous process and unwavering commitment to the Cavalier King Charles Spaniel breed.
                </p>
                <p>
                  Our comprehensive 12-week development program ensures that every puppy receives the optimal start in life. We combine early neurological stimulation with careful socialization and strict veterinary oversight to raise confident, healthy, and well-adjusted companions.
                </p>
              </div>
            </div>

            <Separator className="bg-border" />

            <div className="space-y-8">
              <h3 className="text-2xl font-semibold text-foreground font-sans">The 12-Week Puppy Development Cycle</h3>
              
              <div className="relative border-l border-border ml-3 space-y-10 pb-4">
                {timelineSteps.map((step, i) => <div key={i} data-index={i} className="relative pl-8 md:pl-10">
                    <span className="absolute -left-[5px] top-1.5 flex size-2.5 items-center justify-center rounded-full bg-primary ring-4 ring-background" />
                    <h4 className="text-xl font-semibold text-foreground font-sans mb-2">{step.title}</h4>
                    <p className="text-muted-foreground font-sans leading-relaxed">{step.description}</p>
                  </div>)}
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-5 lg:sticky lg:top-24 space-y-8">
            <AspectRatio ratio={3 / 4} className="bg-muted overflow-hidden">
              <img src="https://milehighcavaliers.10web.cloud/wp-content/uploads/2026/03/Leo-and-Pohko-babies-.jpg" alt="High-key portrait of a Cavalier King Charles Spaniel" className="object-cover w-full h-full transition-transform duration-700 hover:scale-105" />
            </AspectRatio>

            <Card className="rounded-none border-border shadow-sm bg-card">
              <CardHeader className="pb-4">
                <CardTitle className="font-sans text-2xl">Breeder Credentials</CardTitle>
                <CardDescription className="font-sans text-base mt-2">Dedicated to the preservation and advancement of the breed health and standard.</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col sm:flex-row flex-wrap gap-3 pb-6">
                <Badge variant="secondary" className="inline-flex items-center justify-center font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden [a&]:hover:bg-secondary/90 rounded-none font-sans text-sm py-1.5 px-3 bg-secondary/10 border border-secondary/20 !text-[hsl(208,7%,46%)] dark:aria-invalid:ring-destructive/40">
                  <ShieldCheck className="mr-2 size-4 text-secondary" /> 
                  AKC Registered
                </Badge>
                <Badge variant="secondary" className="inline-flex items-center justify-center font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden [a&]:hover:bg-secondary/90 rounded-none font-sans text-sm py-1.5 px-3 bg-primary/10 border border-primary/20 !text-[hsl(208,7%,46%)] dark:aria-invalid:ring-destructive/40">
                  <Award className="mr-2 size-4 text-primary" /> 
                  CKCSC Member
                </Badge>
              </CardContent>
              <CardFooter className="pt-0">
                <Button asChild className="w-full rounded-none font-sans text-base h-12" size="lg">
                  
                </Button>
              </CardFooter>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}